import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';

import { SecaoFormulario } from '@/ui/patterns/secao-formulario';
import { Button } from '@/ui/primitives/button';
import { Checkbox } from '@/ui/primitives/checkbox';
import { Campo, Input } from '@/ui/primitives/input';
import { Select } from '@/ui/primitives/select';
import { Skeleton } from '@/ui/primitives/skeleton';
import {
  PARAMETROS_CLASSIFICACAO_PADRAO,
  type ParametrosClassificacao,
  parametrosClassificacaoSchema,
} from '@fiscoai/contracts';

import { useDefinirParametrosClassificacao, useParametrosClassificacao } from './api';
import { gravar } from './formulario';

/** Parâmetros da classificação dos itens (etapa 2) — nada do grupo fixo no código (ADR 0022). */
export function SecaoClassificacao({ id, podeEditar }: { id: string; podeEditar: boolean }) {
  const consulta = useParametrosClassificacao(id);
  const definir = useDefinirParametrosClassificacao(id);
  const form = useForm<ParametrosClassificacao>({
    resolver: zodResolver(parametrosClassificacaoSchema),
    values: consulta.data ?? PARAMETROS_CLASSIFICACAO_PADRAO,
  });
  const enviar = form.handleSubmit((dados) =>
    gravar(() => definir.mutateAsync(dados), 'Parâmetros de classificação salvos'),
  );
  if (consulta.isPending) return <Skeleton className="h-48" />;

  return (
    <SecaoFormulario
      titulo="Classificação"
      descricao="Como os itens viram grupo contábil e quais alertas aparecem na conferência."
      aoEnviar={() => void enviar()}
      acoes={
        podeEditar &&
        form.formState.isDirty && (
          <Button type="submit" variante="primario" carregando={definir.isPending}>
            Salvar classificação
          </Button>
        )
      }
    >
      <fieldset disabled={!podeEditar} className="grid gap-4 sm:grid-cols-2">
        <legend className="sr-only">Parâmetros de classificação</legend>
        <Campo
          rotulo="Uso e consumo sem centro de custo"
          ajuda="Grupo contábil enquanto não há centro de custo (chega com o axor, etapa 3)."
        >
          {(a) => (
            <Controller
              control={form.control}
              name="grupoPadraoSemCentroCusto"
              render={({ field }) => (
                <Select
                  {...a}
                  valor={field.value}
                  aoMudar={field.onChange}
                  opcoes={[
                    { valor: 'DESPESA', rotulo: 'Despesa' },
                    { valor: 'CUSTO', rotulo: 'Custo' },
                  ]}
                />
              )}
            />
          )}
        </Campo>
        <Campo
          rotulo="Alertar nota emitida há mais de (dias)"
          ajuda="Conferência C11: nota antiga pode cair em período já fechado."
        >
          {(a) => (
            <Input
              type="number"
              min={1}
              {...a}
              {...form.register('diasAlertaEmissaoAntiga', { valueAsNumber: true })}
            />
          )}
        </Campo>
        <Campo
          rotulo="Limite do ativo por unidade (R$)"
          ajuda="Até este valor, ativo pode ir direto para despesa (RIR/2018, art. 313) — gera alerta."
        >
          {(a) => (
            <Input inputMode="decimal" {...a} {...form.register('limiteAtivoValorUnitario')} />
          )}
        </Campo>
        <Controller
          control={form.control}
          name="usaInsumoServicoComunicacao"
          render={({ field }) => (
            <Checkbox
              rotulo="Insumo de serviço de comunicação (x126)"
              descricao="Compra para prestação de serviço sujeito ao ICMS — depende da confirmação do fiscal (pergunta 13)."
              marcado={field.value}
              aoMudar={field.onChange}
            />
          )}
        />
      </fieldset>
    </SecaoFormulario>
  );
}
